import { useMutation } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';
import { match } from 'ts-pattern';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import type { PopoverContentProps } from '@nesso/ui/primitives/popover';

import { PermissionGate } from '@/entities/practice-member';
import { type TreatmentApproach, useUpdateSessionOptions } from '@/entities/session';
import {
    type SessionNoteSettings,
    type SessionNoteType,
    useGenerateNoteOptions,
    useIsGenerationAvailable,
} from '@/entities/session-note';
import { TooltipButton } from '@/shared/ui/common/tooltip-button';

import { EditTreatmentApproachesContent } from './edit-treatment-approaches/edit-treatment-approaches-content';
import { EditTreatmentApproachesDialog } from './edit-treatment-approaches/edit-treatment-approaches-dialog';
import { EditTreatmentApproachesPopover } from './edit-treatment-approaches/edit-treatment-approaches-popover';

type Props = {
    sessionId: string;
    selectedApproaches: TreatmentApproach[];
    type: SessionNoteType;
    settings: SessionNoteSettings;
    popoverContentProps: Omit<PopoverContentProps, 'children'>;
    buttonClassName: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function EditTreatmentApproaches({
    sessionId,
    selectedApproaches,
    type,
    settings,
    popoverContentProps,
    buttonClassName,
    open,
    onOpenChange,
}: Props) {
    const isXs = useBreakpointDown('xs');
    const generateNoteMutation = useMutation(useGenerateNoteOptions(sessionId));
    const updateSessionMutation = useMutation(useUpdateSessionOptions(sessionId));
    const isGenerationAvailable = useIsGenerationAvailable(sessionId);
    const isDisabled = !isGenerationAvailable;

    const handleUpdate = async (treatmentApproaches: TreatmentApproach[]) => {
        if (isDisabled) return;
        onOpenChange(false);
        const approachesChanged =
            treatmentApproaches.length !== selectedApproaches.length ||
            !treatmentApproaches.every((a) => selectedApproaches.includes(a));

        if (approachesChanged) {
            await updateSessionMutation.mutateAsync({
                id: sessionId,
                treatmentApproaches,
            });
            await generateNoteMutation.mutateAsync({
                sessionId,
                type,
                settings,
            });
        }
    };

    return (
        <PermissionGate
            requiredAccessSources={['CLINICIAN', 'NOTES_BASE', 'NOTES_ACTIVE']}
            fallback={(missing) => (
                <TooltipButton
                    variant="ghost"
                    size="icon"
                    className={buttonClassName}
                    aria-label="Edit treatment approach"
                    disabled
                    tooltip={match(missing)
                        .when(
                            (m) => m.includes('NOTES_BASE'),
                            () => 'Add Notes to your plan to edit your notes',
                        )
                        .when(
                            (m) => m.includes('NOTES_ACTIVE'),
                            () => 'Update billing information to edit your notes',
                        )
                        .otherwise(() => 'This is currently unavailable')}
                >
                    <PencilIcon className="w-3 h-3" />
                </TooltipButton>
            )}
        >
            {isXs ? (
                <EditTreatmentApproachesDialog
                    open={open}
                    buttonClassName={buttonClassName}
                    disabled={isDisabled}
                    onOpenChange={onOpenChange}
                >
                    <EditTreatmentApproachesContent
                        variant="dialog"
                        selectedApproaches={selectedApproaches}
                        isOpened={open}
                        onSubmit={handleUpdate}
                        onCancel={() => onOpenChange(false)}
                    />
                </EditTreatmentApproachesDialog>
            ) : (
                <EditTreatmentApproachesPopover
                    open={open}
                    buttonClassName={buttonClassName}
                    disabled={isDisabled}
                    popoverContentProps={popoverContentProps}
                    onOpen={onOpenChange}
                >
                    <EditTreatmentApproachesContent
                        variant="popover"
                        selectedApproaches={selectedApproaches}
                        isOpened={open}
                        onSubmit={handleUpdate}
                        onCancel={() => onOpenChange(false)}
                    />
                </EditTreatmentApproachesPopover>
            )}
        </PermissionGate>
    );
}
